/**
 * How a `MemberDevice` row reads on the two approval screens (spec 6.7, screens
 * 2 and 3).
 *
 * The API answers wire values: `platform` is `ios`, `android` or `web`, and
 * every timestamp is an ISO 8601 string. A member deciding "is this really my
 * phone?" needs neither, and an `Invalid Date` on the screen where they decide
 * is worse than a vaguer sentence that is true.
 *
 * `dayjs` is the repo's date library and reads the device's own zone.
 */
import dayjs from 'dayjs';

/**
 * `web` exists because the API accepts it for the Playwright build under
 * `courses-notes-web` (spec 6.10); a member never registers one in production.
 */
export const PLATFORM_LABELS = Object.freeze({
  ios: 'iPhone or iPad',
  android: 'Android',
  web: 'Web browser',
});

/** Shown to a member, so a date they read the way they write it. */
export const DEVICE_TIME_FORMAT = 'D MMM YYYY, h:mm A';

/**
 * A platform this build has never heard of belongs to a newer app, which is a
 * reason to show the raw value, not to render `undefined` on a screen someone
 * is making a security decision from.
 *
 * @param {string|null|undefined} platform
 * @returns {string}
 */
export function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform || 'Unknown device';
}

/**
 * @param {string|null|undefined} iso
 * @returns {string|null} the formatted time, or null when there is nothing real to show
 */
function deviceTime(iso) {
  if (!iso) return null;
  const at = dayjs(iso);
  return at.isValid() ? at.format(DEVICE_TIME_FORMAT) : null;
}

/**
 * `lastSeenAt` is advisory — the API sets it from a client-chosen
 * `X-Device-Id` — so it is a recognition aid, never an authorisation signal.
 *
 * @param {string|null|undefined} iso a device's `lastSeenAt`
 * @returns {string}
 */
export function lastSeenLabel(iso) {
  const at = deviceTime(iso);
  return at ? `Last used ${at}` : 'Not used yet';
}

/**
 * @param {string|null|undefined} iso a pending device's `createdAt`
 * @returns {string}
 */
export function requestedAtLabel(iso) {
  const at = deviceTime(iso);
  return at ? `Requested ${at}` : 'Requested just now';
}
